import React, { createContext, useState, useEffect, useContext } from 'react';
import { checkApiConnection } from '../services/domain/networkService';
import { checkApiHealth } from '../services/healthService';

// Define the context type with expanded functionality
interface SupabaseContextType {
  isSupabaseAvailable: boolean;
  isBackendAvailable: boolean;
  isLoading: boolean;
  refreshConnections: () => Promise<{
    backendHealthy: boolean;
    supabaseConnected: boolean;
  }>;
  preferredDataSource: 'backend' | 'supabase';
  setPreferredDataSource: (source: 'backend' | 'supabase') => void;
}

// Create context with default values
const SupabaseContext = createContext<SupabaseContextType>({
  isSupabaseAvailable: false,
  isBackendAvailable: false,
  isLoading: true,
  refreshConnections: async () => ({ backendHealthy: false, supabaseConnected: false }),
  preferredDataSource: 'backend',
  setPreferredDataSource: () => {},
});

/**
 * Provider component that wraps your app and provides Supabase connection state
 */
export const SupabaseProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isSupabaseAvailable, setIsSupabaseAvailable] = useState<boolean>(false);
  const [isBackendAvailable, setIsBackendAvailable] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [preferredDataSource, setPreferredDataSource] = useState<'backend' | 'supabase'>(
    localStorage.getItem('preferredDataSource') === 'supabase' ? 'supabase' : 'backend'
  );

  // Function to check connections that can be triggered from consumers
  const refreshConnections = async () => {
    try {
      // Check backend health first
      const backendHealth = await checkApiHealth();
      setIsBackendAvailable(backendHealth.status === 'healthy');
      
      // Then check Supabase connection
      const supabaseConnected = await checkApiConnection();
      setIsSupabaseAvailable(supabaseConnected);
      
      return { backendHealthy: backendHealth.status === 'healthy', supabaseConnected };
    } catch (err) {
      console.error('Error checking connections:', err);
      setIsBackendAvailable(false);
      setIsSupabaseAvailable(false);
      return { backendHealthy: false, supabaseConnected: false };
    }
  };

  // Handle data source preference changes
  const handleDataSourceChange = (source: 'backend' | 'supabase') => {
    setPreferredDataSource(source);
    localStorage.setItem('preferredDataSource', source);
    console.log(`Data source preference set to: ${source}`);
  };

  useEffect(() => {
    // Initial connection check
    const initialCheck = async () => {
      await refreshConnections();
      setIsLoading(false);
    };

    initialCheck();

    // Set up interval to periodically check connections
    const intervalId = setInterval(() => {
      if (navigator.onLine) {
        refreshConnections();
      }
    }, 60000); // Check every minute

    return () => {
      clearInterval(intervalId);
    };
  }, []);

  // Context value with expanded functionality
  const contextValue: SupabaseContextType = {
    isSupabaseAvailable,
    isBackendAvailable,
    isLoading,
    refreshConnections,
    preferredDataSource,
    setPreferredDataSource: handleDataSourceChange
  };

  return (
    <SupabaseContext.Provider value={contextValue}>
      {children}
    </SupabaseContext.Provider>
  );
};

/**
 * Custom hook to access the Supabase context
 * This is a named export declaration which is compatible with Fast Refresh
 */
export function useSupabase() {
  return useContext(SupabaseContext);
}

/**
 * Component to display the current data source status
 * Creating a separate component ensures React Fast Refresh works properly
 */
export const DataSourceIndicator: React.FC = () => {
  const {
    isBackendAvailable,
    isSupabaseAvailable,
    preferredDataSource,
    setPreferredDataSource
  } = useSupabase();

  const handleToggleSource = () => {
    setPreferredDataSource(preferredDataSource === 'backend' ? 'supabase' : 'backend');
  };

  return (
    <div className="fixed bottom-16 right-4 z-30 bg-white rounded-lg shadow-lg p-3">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-medium">Data Source:</span>
        <button 
          onClick={handleToggleSource}
          className="text-xs bg-neutral-100 hover:bg-neutral-200 px-2 py-1 rounded transition-colors"
        >
          Switch
        </button>
      </div>
      
      <div className="text-sm">
        <div className="flex items-center mb-1">
          <span className={`w-2 h-2 rounded-full mr-2 ${isBackendAvailable ? 'bg-green-500' : 'bg-red-500'}`}></span>
          <span className={`${preferredDataSource === 'backend' ? 'font-semibold' : ''}`}>
            Backend API
          </span>
          {preferredDataSource === 'backend' && <span className="ml-1 text-xs text-accent-500">✓</span>}
        </div>
        <div className="flex items-center">
          <span className={`w-2 h-2 rounded-full mr-2 ${isSupabaseAvailable ? 'bg-green-500' : 'bg-red-500'}`}></span>
          <span className={`${preferredDataSource === 'supabase' ? 'font-semibold' : ''}`}>
            Supabase
          </span>
          {preferredDataSource === 'supabase' && <span className="ml-1 text-xs text-accent-500">✓</span>}
        </div>
      </div>
    </div>
  );
};